import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { initSessionManager } from '../services/sessionService';
import PinSetupModal from '../components/auth/PinSetupModal';

/**
 * AuthCallback - Handles token storage after magic link verification.
 * Shows PIN setup modal for internal users who don't have a PIN yet.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [jwtToken, setJwtToken] = useState(null);
  const [redirectPath, setRedirectPath] = useState('/');

  useEffect(() => {
    const handleCallback = () => {
      try {
        const token = searchParams.get('token');
        const role = searchParams.get('role');
        const redirect = searchParams.get('redirect') || '/';
        const userData = searchParams.get('user');
        const hasPin = searchParams.get('has_pin');

        if (!token) {
          setError('No se recibio token de autenticacion');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        // Clear ALL old tokens first
        localStorage.removeItem('portal_token');
        localStorage.removeItem('portal_user');
        localStorage.removeItem('worker_token');
        localStorage.removeItem('worker_user');
        localStorage.removeItem('client_token');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('user');

        // JWT lives only in the httpOnly access_token cookie set by the backend.
        // We persist only user metadata here for UI rendering.

        // Store user data if provided
        if (userData) {
          try {
            const parsedUser = JSON.parse(decodeURIComponent(userData));
            localStorage.setItem('portal_user', JSON.stringify(parsedUser));
            localStorage.setItem('user', JSON.stringify(parsedUser));
            if (role === 'worker') localStorage.setItem('worker_user', JSON.stringify(parsedUser));
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }

        // Initialize session manager
        initSessionManager();

        // Check if internal user needs PIN setup
        if (role !== 'client' && hasPin === '0') {
          setJwtToken(token);
          setRedirectPath(redirect);
          setShowPinSetup(true);
          return;
        }

        // Navigate to final destination
        setTimeout(() => {
          navigate(redirect, { replace: true });
        }, 100);

      } catch (err) {
        console.error('Auth callback error:', err);
        setError('Error al procesar la autenticacion');
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const handlePinSetupComplete = () => {
    setShowPinSetup(false);
    navigate(redirectPath, { replace: true });
  };

  if (showPinSetup) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <PinSetupModal token={jwtToken} onComplete={handlePinSetupComplete} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <p className="text-[#AEAEB2] text-sm">Redirigiendo al inicio de sesion...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
        <p className="text-[#1D1D1F] font-medium">Configurando tu sesion...</p>
        <p className="text-[#AEAEB2] text-sm mt-2">Un momento por favor</p>
      </div>
    </div>
  );
}
